import cn from 'classnames';
import {
  CommonCell,
  SectorCell,
} from '@/creditflowresearch/ui/common/components/TableCells';
import { commonCellCallback } from '@/condor/ui/common/Layout/helpers';
import { getBondsDate } from '@/creditflowresearch/ui/components/bonds/helpers';
import {
  SelectCell,
  SelectAllCell,
  PositionsLastCell,
  PositionSizeCell,
  PositionBondNameCell,
  CurrencyCell,
} from './cellCallbacks';
import DealLink from './DealLink';
import DealLinkWithStatus from './DealLinkWithStatus';
import RatingsCell from './RatingsCell';
import URL from '@/creditflowresearch/Router/urlConstants';
import { ABS } from '@/creditflowresearch/domain/AssetClass';
import { BWICS, INVENTORIES } from '@/creditflowresearch/domain/BondType';
import { formatPositionSize } from '@/creditflowresearch/ui/components/portfolioMonitor/helpers';

import alignStyles from '@creditflowresearch/uilib/styles/helpers/align.module.scss';
import spaceStyles from '@creditflowresearch/uilib/styles/helpers/spaces.module.scss';

const COLUMN_NAMES = {
  ID: 'id',
  SECTOR: 'sector',
  NAME: 'name',
  CUSIP: 'cusip',
  CURRENCY: 'currencyName',
  POSITION: 'position',
  RATING: 'rating',
  BWICS_COUNT: 'bwicsCount',
  INVENTORIES_COUNT: 'inventoriesCount',
  WTD_TRACE_COUNT: 'wtdTraceCount',
  MTD_TRACE_COUNT: 'mtdTraceCount',
  YTD_TRACE_COUNT: 'ytdTraceCount',
  LAST_TRACE_DATE: 'lastTraceDate',
  LAST_PRICE: 'lastPrice',
  PRICE_PAID: 'pricePaid',
  AGING: 'aging',
  AT_TRACE_COUNT: 'atTraceCount',
};

export const columns = [
  {
    name: COLUMN_NAMES.ID,
    headCallback: SelectAllCell,
    cellCallback: SelectCell,
    minWidth: 41,
  },
  {
    name: COLUMN_NAMES.SECTOR,
    title: 'Sector',
    sortable: true,
    cellCallback: SectorCell,
    tooltipContent: 'The sector and subsector of the security',
  },
  {
    name: COLUMN_NAMES.NAME,
    title: 'Bond',
    sortable: true,
    cellCallback: PositionBondNameCell,
    tooltipContent: 'The security’s TICKER and class',
    minWidth: 130,
  },
  {
    name: COLUMN_NAMES.CUSIP,
    title: 'Cusip',
    cellCallback: commonCellCallback,
    tooltipContent: 'The CUSIP identifier',
    className: alignStyles.alignRight,
    minWidth: 102,
  },
  {
    name: COLUMN_NAMES.CURRENCY,
    title: 'CCY',
    sortable: true,
    cellCallback: (cellProps) => <CurrencyCell positionColumnName={ COLUMN_NAMES.POSITION } { ...cellProps } />,
    tooltipContent: 'Currency',
    className: alignStyles.alignCenter,
    minWidth: 75,
  },
  {
    name: COLUMN_NAMES.POSITION,
    title: 'Position',
    sortable: true,
    cellCallback: (cellProps) => <PositionSizeCell format={ formatPositionSize } { ...cellProps } />,
    tooltipContent: 'Position Size',
    className: alignStyles.alignRight,
    minWidth: 122,
  },
  {
    name: COLUMN_NAMES.RATING,
    title: 'Rating',
    cellCallback: RatingsCell,
    tooltipContent: 'The security rating at new issue',
    className: alignStyles.alignLeft,
    minWidth: 112,
  },
  {
    name: COLUMN_NAMES.BWICS_COUNT,
    title: '# bwics',
    sortable: true,
    minWidth: 72,
    cellCallback: (cellProps) => (
      <DealLinkWithStatus
        path={ `${ URL.BONDS_BWICS }-${ ABS }` }
        bondType={ BWICS }
        { ...cellProps }
      />
    ),
    tooltipContent: '# of BWICS of this security',
    className: cn(alignStyles.alignCenter, spaceStyles.pn),
  },
  {
    name: COLUMN_NAMES.INVENTORIES_COUNT,
    title: '# inventory',
    sortable: true,
    minWidth: 91,
    cellCallback: (cellProps) => (
      <DealLinkWithStatus
        path={ `${ URL.BONDS_INVENTORIES }-${ ABS }` }
        bondType={ INVENTORIES }
        { ...cellProps }
      />
    ),
    tooltipContent: '# of positions in dealer inventories ',
    className: cn(alignStyles.alignCenter, spaceStyles.pn),
  },
  {
    name: COLUMN_NAMES.WTD_TRACE_COUNT,
    title: 'WTD Trace',
    sortable: true,
    cellCallback: (cellProps) => <DealLink path={ `${ URL.TRACE }-${ ABS }` } { ...cellProps } />,
    tooltipContent: '# of trades reported on TRACE week to date.',
    className: alignStyles.alignRight,
  },
  {
    name: COLUMN_NAMES.MTD_TRACE_COUNT,
    title: 'MTD Trace',
    sortable: true,
    cellCallback: (cellProps) => <DealLink path={ `${ URL.TRACE }-${ ABS }` } { ...cellProps } />,
    tooltipContent: '# of trades reported on TRACE month to date.',
    className: alignStyles.alignRight,
  },
  {
    name: COLUMN_NAMES.YTD_TRACE_COUNT,
    title: 'YTD Trace',
    sortable: true,
    cellCallback: (cellProps) => <DealLink path={ `${ URL.TRACE }-${ ABS }` } { ...cellProps } />,
    tooltipContent: '# of trades reported on TRACE year to date.',
    className: alignStyles.alignRight,
  },
  {
    name: COLUMN_NAMES.AT_TRACE_COUNT,
    title: 'At Trace',
    sortable: true,
    cellCallback: (cellProps) => <DealLink path={ `${ URL.TRACE }-${ ABS }` } { ...cellProps } />,
    tooltipContent: '# of trades reported on TRACE for all time',
    className: alignStyles.alignRight,
  },
  {
    name: COLUMN_NAMES.LAST_TRACE_DATE,
    title: 'Last Trace',
    sortable: true,
    cellCallback: (cellProps) => (cellProps.cellData ? getBondsDate(cellProps.cellData, true) : '-'),
    tooltipContent: 'The most recent trade date per TRACE',
    className: alignStyles.alignRight,
    minWidth: 108,
  },
  {
    name: COLUMN_NAMES.LAST_PRICE,
    title: 'Last Price',
    sortable: true,
    cellCallback: CommonCell,
    tooltipContent: 'The dollar price reported on the most recent trade date',
    className: alignStyles.alignRight,
  },
  {
    name: COLUMN_NAMES.PRICE_PAID,
    title: 'Price Paid',
    sortable: true,
    cellCallback: CommonCell,
    tooltipContent: 'The dollar price you paid for the security.',
    className: alignStyles.alignRight,
  },
  {
    name: COLUMN_NAMES.AGING,
    title: 'Aging',
    sortable: true,
    cellCallback: PositionsLastCell,
    tooltipContent: 'The # of days you’ve held this security.',
    className: alignStyles.alignRight,
  },
];
